import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { saveAs } from 'file-saver';
import { NewPracticeFormComponent } from '@pages/practices-management/practices/new-practice/new-practice-form/new-practice-form.component';
import { PracticeModel } from '@/models/practice.model';
import { LoggerService } from '@services/logger.service';
import { Registry } from '@/registry.service';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { UserService } from '@services/admin/user.service';
import { PracticesService } from '@services/practices.service';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-detail-practice-form',
  templateUrl: '../../../../../components/dynamic-form/dynamic-form.component.html',
  styleUrls: ['../../../../../components/dynamic-form/dynamic-form.component.scss'],
})
export class DetailPracticeFormComponent
  extends NewPracticeFormComponent
  implements OnInit, OnChanges
{
  @Input() eventUpdateProvvigioneAccessori: Subject<any>;

  constructor(
    protected router: Router,
    protected userService: UserService,
    protected practiceService: PracticesService,
    protected toastr: ToastrService,
    public dialog: MatDialog,
  ) {
    super(router, userService, practiceService, toastr, dialog);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.practiceTypeDisabled = true;
    this.fieldArriveFromCross = true;

    if (Object.values(this.data).length !== 0) {
      LoggerService.log('Update field after load data');

      this.setCommissionFields();
      this.refreshDataPracticeType(this.data.tipoPratica);
      this.securityDepositChanges(this.data.depositoCauzionale);
      this.setVeicoloAziendaleSelected(this.data.veicoloAziendale);

      if (this.data.marca && this.data.marca.id.length !== 0) {
        this.brandsChanges(this.data.marca.id);
      }

      if (this.data.tipoCliente && this.data.tipoCliente.length !== 0) {
        this.typeClientChanges(this.data.tipoCliente);
      }

      if (this.data.tipoPagamento && this.data.tipoPagamento.id.length !== 0) {
        this.typePaymentChanges(this.data.tipoPagamento);
      }

      this.updateSellerFields();

      // Trigger segnalatoreChanges if segnalatore is true at page load
      // Use setTimeout to ensure form is fully populated before calling segnalatoreChanges
      if (this.data.segnalatore) {
        setTimeout(() => {
          // Ensure form has the segnalatore value before calling segnalatoreChanges
          if (this.form.controls['segnalatore']) {
            this.form.controls['segnalatore'].setValue(this.data.segnalatore);
          }
          this.segnalatoreChanges();
        }, 0);
      }
    }

    Registry.getInstance().set(DetailPracticeFormComponent.name, this);

    // Subscribe to the event after initialization
    if (this.eventUpdateProvvigioneAccessori) {
      this.eventUpdateProvvigioneAccessori.subscribe(() => {
        this.updateProvvigioneAccessori();
      });
    }
  }

  public ngOnChanges() {
    super.ngOnChanges();

    this.updateClientType();
  }

  public onSubmit() {
    this.submitButtonLoading = true;
    let practice = <PracticeModel>this.form.getRawValue();
    practice = this.setDefaultValues(practice);
    practice = this.setupVehicleData(practice);

    this.practiceService.savePractice(practice).subscribe({
      next: (response) => {
        this.toastr.success('Pratica salvata correttamente!');

        Registry.getInstance().get('PracticeDetailsComponent').data = <PracticeModel>response;
        this.submitButtonLoading = false;
      },
      error: (error) => {
        LoggerService.error('savePractice failed!');
        LoggerService.error(error);

        this.manageErrorMessages(error);

        setTimeout(() => {
          Registry.getInstance().set('saveInProgress', false);
          this.submitButtonLoading = false;
        }, 1000);
      },
      complete: () => {
        LoggerService.log('Request done');

        setTimeout(() => {
          Registry.getInstance().set('saveInProgress', false);
        }, 1000);
      },
    });
  }

  protected thirdButtonAction() {
    this.practiceService.downloadPraticaPdf(this.data.id).subscribe((res) => {
      saveAs(res, 'Pratica' + this.data.id + '.pdf');
    });
  }

  private updateSellerFields() {
    const seller = this.data.venditore;

    if (seller) {
      LoggerService.log('AGENZIA SUBAGENTE', seller.agenziaSubagente);

      if (seller.agenziaSubagente != null && seller.users) {
        this.subAgentUsers = [];

        for (const user of seller.users) {
          this.subAgentUsers.push({
            key: user.username,
            value: user.username,
          });
        }

        this.form.controls['utenteSubagente'].enable();
      }
    }
  }

  private setCommissionFields() {
    this.signalman = Boolean(this.data.segnalatore ?? false);
    this.sellerSelected = Boolean(this.data.codiceVenditore ?? false);

    this.setupMeta();
  }

  private setupVehicleData(practice: PracticeModel) {
    practice.id = this.data.id;
    practice.codiceModello = practice.codiceModello ?? this.data.codiceModello;
    practice.targa = practice.targa ?? this.data.targa;
    practice.siglaModello = practice.siglaModello ?? this.data.siglaModello;
    practice.descrizioneModello = practice.descrizioneModello ?? this.data.descrizioneModello;
    practice.telaio = practice.telaio ?? this.data.telaio;
    practice.statoOrdineVgi = practice.statoOrdineVgi ?? this.data.statoOrdineVgi;
    practice.codiceColore = practice.codiceColore ?? this.data.codiceColore;
    practice.codiceColoreInterno = practice.codiceColoreInterno ?? this.data.codiceColoreInterno;
    practice.descrizioneColoreInterno =
      practice.descrizioneColoreInterno ?? this.data.descrizioneColoreInterno;
    practice.descrizioneColore = practice.descrizioneColore ?? this.data.descrizioneColore;
    practice.bpUtilizzatore = practice.bpUtilizzatore ?? this.data.bpUtilizzatore;
    practice.denominazioneUtilizzatore =
      practice.denominazioneUtilizzatore ?? this.data.denominazioneUtilizzatore;
    practice.indirizzoUtilizzatore =
      practice.indirizzoUtilizzatore ?? this.data.indirizzoUtilizzatore;
    practice.cittaUtilizzatore = practice.cittaUtilizzatore ?? this.data.cittaUtilizzatore;
    practice.patenteUtilizzatore = practice.patenteUtilizzatore ?? this.data.patenteUtilizzatore;
    practice.patenteRilasciataDaUtilizzatore =
      practice.patenteRilasciataDaUtilizzatore ?? this.data.patenteRilasciataDaUtilizzatore;
    practice.pivaUtilizzatore = practice.pivaUtilizzatore ?? this.data.pivaUtilizzatore;
    practice.codiceFiscaleUtilizzatore =
      practice.codiceFiscaleUtilizzatore ?? this.data.codiceFiscaleUtilizzatore;
    practice.emailUtilizzatore = practice.emailUtilizzatore ?? this.data.emailUtilizzatore;

    return practice;
  }
}
